import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TruckJourneyService } from '../../services/truck-journey.service';
import { Location } from '@angular/common';
import { TruckJourneyAssignmentLoad, TruckJourneyInfoOpenable, TruckJourneySearchCriteria } from '../../models/truck-journey.model';
import { finalize, map } from 'rxjs/operators';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { LoaderService } from '../../../../shared/services/loader.service';
import { ComponentCanDeactivateByField } from '../../../../core/guards/pending-changes-action.guard';
import * as moment from 'moment';
import { Moment } from 'moment';
import { DataFiltersComponent } from '../../../../shared/data-filters/data-filters.component';
import { Observable } from 'rxjs';
import { TruckJourneyRequestMapper } from '../../mappers/truck-journey-request.mapper';
import { TranslatedToastrService } from '../../../../shared/services/translated-toastr.service';
import { NavigationService } from '../../../../shared/services/navigation.service';

export enum SubScreen {
  LOADS_SELECT = 'LOADS_SELECT',
  TRUCK_JOURNEYS_SELECT = 'TRUCK_JOURNEYS_SELECT'
}

@Component({
  templateUrl: './truck-journey-load-assignment-page.component.html',
  styleUrls: ['./truck-journey-load-assignment-page.component.scss']
})
export class TruckJourneyLoadAssignmentPageComponent implements OnInit, ComponentCanDeactivateByField {

  @ViewChild('dataFilters', {static: true}) private filtersComponent: DataFiltersComponent;

  public carrierId: string;
  public preventNavigationFrom = false;
  public truckJourneys: TruckJourneyInfoOpenable[] = [];
  public selectedTruckJourneyIndex: number = undefined;
  public loadAssignments: TruckJourneyAssignmentLoad[];
  public showScreen = SubScreen.LOADS_SELECT;
  public isLoading: Observable<boolean>;
  public isValidSearch: boolean;
  public searchLaunched = false;
  public yesterday: Moment = moment().startOf('day').add(-1, 'days');
  public SubScreen = SubScreen;

  private filtersBase: TruckJourneySearchCriteria = {
    carrierId: undefined,
    loadNumber: undefined,
    vehicle: '',
    plannedDepartureFrom: moment().startOf('day'),
    plannedDepartureTo: undefined,
    pickLocationGlids: [],
    dropLocationGlids: []
  };

  public filters: TruckJourneySearchCriteria = {...this.filtersBase};

  constructor(
    protected route: ActivatedRoute,
    private truckJourneyService: TruckJourneyService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private navigation: NavigationService,
    private translatedToastrService: TranslatedToastrService,
    public location: Location
  ) {
    this.isLoading = this.loader.getObservable().pipe(map(loadParam => loadParam.loading));
  }

  public ngOnInit(): void {
    if (this.filtersComponent) {
      this.filtersComponent.getResetObservable().subscribe(() => this.resetFilters());
      this.filtersComponent.getApplyObservable().subscribe(() => this.searchTruckJourneys());
    }
    this.route.queryParams.subscribe(({carrierId}): void => {
      this.carrierId = carrierId;
      this.resetFilters();
    });
  }

  public searchTruckJourneys(): void {
    this.searchLaunched = true;
    this.loader.startLoad(true);
    this.truckJourneyService.searchTruckJourneys({
      ...this.filters,
      plannedDepartureFrom: moment(this.filters.plannedDepartureFrom),
      plannedDepartureTo: moment(this.filters.plannedDepartureFrom)
    })
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(truckJourneys => {
        this.truckJourneys = truckJourneys.map(truckJourney => ({...truckJourney, opened: false}));
      }, error => {
        this.errorHandler.handleHttpError(error, 'truck-journeys.list.search-error');
      });
  }

  public resetFilters(): void {
    this.filters = {...this.filtersBase, carrierId: this.carrierId};
    this.searchLaunched = false;
  }

  public checkSearchValidity(): void {
    this.isValidSearch = this.filters.vehicle && this.filters.vehicle.length > 0 && !!this.filters.plannedDepartureFrom;
  }

  public changeLoadSelection(): void {
    this.showScreen = SubScreen.LOADS_SELECT;
  }

  public goToTruckJourneySelection(): void {
    this.showScreen = SubScreen.TRUCK_JOURNEYS_SELECT;
  }

  public exitLoadAssignmentPage(): void {
    this.location.back()
  }

  public assignLoads(): void {
    if (this.selectedTruckJourneyIndex !== 0 && !this.selectedTruckJourneyIndex) {
      this.translatedToastrService.showErrorMessage('truck-journeys.load-assignment.select-truckjourney-or-assign-new');
    } else if (!this.loadAssignments || this.loadAssignments.length === 0) {
      this.translatedToastrService.showErrorMessage('truck-journeys.load-assignment.select-loads-to-assign');
    } else if (this.selectedTruckJourneyIndex === -1) {
      // New truckJourney
      const request = TruckJourneyRequestMapper.toRequest(
        this.filters.carrierId,
        this.filters.vehicle,
        this.filters.plannedDepartureFrom,
        this.loadAssignments,
        this.truckJourneys.length
      );
      this.loader.startLoad(true);
      this.truckJourneyService.createTruckJourney(request)
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(savedTruckJourneyId => {
            this.translatedToastrService.showSuccessMessage('truck-journeys.load-assignment.create-success');
            this.navigation.truckJourneys()
              .detail(savedTruckJourneyId, this.carrierId)
              .go();
          },
          error => this.errorHandler.handleHttpError(error, 'truck-journeys.load-assignment.assign-error')
        );
    } else {
      const truckJourneyToUpdate = this.truckJourneys[this.selectedTruckJourneyIndex];
      truckJourneyToUpdate.loads.push(...this.loadAssignments);
      this.loader.startLoad(true);
      this.truckJourneyService.updateTruckJourney(TruckJourneyRequestMapper.fromTruckJourneyInfo(truckJourneyToUpdate, this.carrierId))
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(savedTruckJourney => {
            this.translatedToastrService.showSuccessMessage('truck-journeys.load-assignment.edit-success');
            this.navigation.truckJourneys()
              .detail(savedTruckJourney.id, this.carrierId)
              .go();
          },
          error => this.errorHandler.handleHttpError(error, 'truck-journeys.load-assignment.assign-error')
        );
    }
  }
}
